import type { PropsWithChildren, FunctionComponent } from 'react'
import { useMemo } from 'react'
import styled from '@emotion/styled'
import isNumber from 'lodash/isNumber'
import isFinite from 'lodash/isFinite'
import { colors, spacing } from '@retailer-platform/shared-components'
import { Text, InformationIconTooltip, Flex } from '@retailer-platform/shared-components/src/tds'
import { ArrowUpIcon, ArrowDownIcon, SubtractIcon, Box } from '@instacart/ids-core'
import { DEFAULT_NO_VALUE_PLACEHOLDER } from '../utils/PickingPerformanceOverview.constants'
import {
  Metric,
  LabelContainer,
  SubLabelContainer,
  Trend,
  TrendDirectionContainer,
  TrendValue,
} from './PerformanceMetric.styles'

const TooltipDisclaimer = styled.div({
  marginTop: spacing.X8,
})

export enum TrendDirection {
  NEGATIVE = 'negative',
  POSITIVE = 'positive',
  UNCHANGED = 'unchanged',
}

interface Props {
  metric: number
  metricDenominator?: number
  metricSymbol?: string
  trendValue?: number
  trendDirection?: TrendDirection
  title: string
  tooltip: string
  tooltipDisclaimer?: string
  noValuePlaceholder?: string
  invertTrendColor?: boolean
}

export const PerformanceMetric: FunctionComponent<PropsWithChildren<Props>> = ({
  metric,
  metricDenominator,
  metricSymbol,
  trendValue,
  trendDirection,
  title,
  tooltip,
  tooltipDisclaimer,
  noValuePlaceholder = DEFAULT_NO_VALUE_PLACEHOLDER,
  invertTrendColor = false,
}) => {
  const { trendColor, TrendIcon } = useMemo(() => {
    const positiveColor = colors.HIGHLIGHT.REGULAR
    const negativeColor = colors.DETRIMENTAL.REGULAR
    switch (trendDirection) {
      case 'negative':
        return {
          trendColor: invertTrendColor ? positiveColor : negativeColor,
          TrendIcon: ArrowDownIcon,
        }
      case 'positive':
        return {
          trendColor: invertTrendColor ? negativeColor : positiveColor,
          TrendIcon: ArrowUpIcon,
        }
      default:
        return { trendColor: colors.GRAYSCALE.X30, TrendIcon: SubtractIcon }
    }
  }, [invertTrendColor, trendDirection])

  // isFinite is used on the trendValue to hide results that are Infinity or NaN,
  // since it is possible to have a pastValue of 0
  return (
    <Metric>
      <LabelContainer>
        <Flex.Row alignItems="center">
          <Text typography="headline">
            {`${metric ?? noValuePlaceholder}${
              metricSymbol && isNumber(metric) ? metricSymbol : ''
            }`}
          </Text>
          {isNumber(metricDenominator) && (
            <Text typography="subtitle" color="systemGrayscale30">{`/${metricDenominator}`}</Text>
          )}
        </Flex.Row>
        {isFinite(trendValue) && (
          <Trend trendColor={trendColor}>
            <TrendDirectionContainer>
              <TrendIcon />
            </TrendDirectionContainer>
            <TrendValue>{trendValue}%</TrendValue>
          </Trend>
        )}
      </LabelContainer>
      <SubLabelContainer>
        <Box css={{ marginRight: spacing.X8 }}>
          <Text typography="accentRegular">{title}</Text>
        </Box>
        <InformationIconTooltip
          position="bottom"
          // TODO: refactor. TDS tooltip only allows strings as label.
          // @ts-ignore
          label={
            <>
              <Text typography="bodyRegular">{tooltip}</Text>
              {tooltipDisclaimer && (
                <TooltipDisclaimer>
                  <Text typography="bodyEmphasized">{tooltipDisclaimer}</Text>
                </TooltipDisclaimer>
              )}
            </>
          }
        />
      </SubLabelContainer>
    </Metric>
  )
}
